import type {
  ArtistProfileErrorCode,
  ArtistProfileResponse,
  SerializedProfileAsset,
  SpotifyProfileImage,
} from 'fym-shared/src/music24/artistProfile';

/**
 * The Spotify button of the header and the photo (design a1, a2, c2.7, c2.8):
 * - `manual`: no Auto upload access. The button is not shown; the sheet shows the steps.
 * - `upgrade`: Free or the trial. A preview never goes to Spotify: "Upgrade to send to Spotify".
 * - `send`: "Upload to Spotify" opens the sheet.
 */
export type SpotifyButtonMode = 'manual' | 'upgrade' | 'send';

type ProfileFacts = Pick<ArtistProfileResponse, 'preview' | 'spotify'>;

/** The mode of the Spotify button. Pure. */
export function spotifyButtonMode(profile: ProfileFacts): SpotifyButtonMode {
  if (profile.spotify.unavailable === 'noAutoUploadAccess') {
    return 'manual';
  }
  if (profile.spotify.unavailable === 'freePlan' || profile.preview) {
    return 'upgrade';
  }
  return 'send';
}

/** The upload state of one image on Spotify (design c2.1–c2.9). */
export type SpotifyImageState =
  | { kind: 'none' }
  | { kind: 'sending' }
  | { kind: 'onSpotify'; at: string }
  | { kind: 'changed' }
  | { kind: 'reconnect' }
  | { kind: 'noPermission' }
  | { kind: 'refused'; detail: string | null }
  | { kind: 'fileGone' }
  | { kind: 'error' };

/** The state of the newest upload of a version (`spotify` of the version). Pure. */
export function spotifyImageState(asset: SerializedProfileAsset | null): SpotifyImageState {
  const upload = asset?.spotify ?? null;
  if (upload === null) {
    return { kind: 'none' };
  }
  switch (upload.state) {
    case 'sending':
      return { kind: 'sending' };
    case 'onSpotify':
      return { kind: 'onSpotify', at: upload.at };
    case 'changedOnSpotify':
      return { kind: 'changed' };
    case 'failed':
      switch (upload.failReason) {
        case 'reconnect':
          return { kind: 'reconnect' };
        case 'noPermission':
          return { kind: 'noPermission' };
        case 'refused':
          return { kind: 'refused', detail: upload.failDetail };
        case 'fileGone':
          return { kind: 'fileGone' };
        case 'error':
        case null:
          return { kind: 'error' };
      }
      return { kind: 'error' };
  }
}

/**
 * What the sheet shows before Send (design c2): the steps with no Auto upload access, Upgrade on
 * Free, Connect with no Spotify for Artists sign-in, "Sign in again" after it ended, "Make it first"
 * with no ready image, else the images to send.
 */
export type SpotifySheetView =
  | { kind: 'manual' }
  | { kind: 'free' }
  | { kind: 'notConnected' }
  | { kind: 'reconnect' }
  | { kind: 'nothing' }
  | { kind: 'send'; images: SpotifyProfileImage[] };

/** The view of the sheet from the profile and the current header and photo. Pure. */
export function spotifySheetView(
  profile: ProfileFacts,
  header: SerializedProfileAsset | null,
  avatar: SerializedProfileAsset | null
): SpotifySheetView {
  const mode = spotifyButtonMode(profile);
  if (mode === 'manual') {
    return { kind: 'manual' };
  }
  if (mode === 'upgrade') {
    return { kind: 'free' };
  }
  if (profile.spotify.unavailable === 'notConnected') {
    return { kind: 'notConnected' };
  }
  if (profile.spotify.unavailable === 'reconnect') {
    return { kind: 'reconnect' };
  }
  const images: SpotifyProfileImage[] = [];
  if (header !== null) {
    images.push('header');
  }
  if (avatar !== null) {
    images.push('avatar');
  }
  return images.length === 0 ? { kind: 'nothing' } : { kind: 'send', images };
}

/** What the sheet does after a refused Send (design c2 notes "Gates"). */
export type SpotifyPostOutcome =
  /** 403 M24_AUTO_UPLOAD_NOT_ENABLED: the steps take the place of Send. */
  | { kind: 'manual' }
  /** 409 M24_PUBLISH_NOT_CONNECTED: the profile reads again and shows Connect. */
  | { kind: 'notConnected' }
  /** 409 M24_PROFILE_RIGHTS_REQUIRED: the rights line must be ticked. */
  | { kind: 'rights' }
  /** 400 M24_PROFILE_UPLOAD_FILE_REFUSED with its reason. */
  | { kind: 'file'; reason: 'preview' | 'tooSmall' | 'notReady' | null }
  /** 409 M24_PUBLISH_ALREADY_LIVE: an upload is on its way already. */
  | { kind: 'busy' }
  | { kind: 'generic' };

function causeOf(error: unknown): { code: string | null; reason: string | null } {
  const record = error as {
    m24Limit?: { code?: unknown; reason?: unknown };
    cause?: { code?: unknown; reason?: unknown };
  } | null;
  const cause = record?.m24Limit ?? record?.cause;
  return {
    code: typeof cause?.code === 'string' ? cause.code : null,
    reason: typeof cause?.reason === 'string' ? cause.reason : null,
  };
}

/** The `M24_*` code of a refused Profile call, or null. Pure. */
export function profileErrorCode(error: unknown): ArtistProfileErrorCode | string | null {
  return causeOf(error).code;
}

/** The outcome of a refused `POST .../artist-profile/spotify`. Pure. */
export function spotifyPostOutcome(error: unknown): SpotifyPostOutcome {
  const { code, reason } = causeOf(error);
  switch (code) {
    case 'M24_AUTO_UPLOAD_NOT_ENABLED':
      return { kind: 'manual' };
    case 'M24_PUBLISH_NOT_CONNECTED':
      return { kind: 'notConnected' };
    case 'M24_PROFILE_RIGHTS_REQUIRED':
      return { kind: 'rights' };
    case 'M24_PROFILE_UPLOAD_FILE_REFUSED':
      return {
        kind: 'file',
        reason: reason === 'preview' || reason === 'tooSmall' || reason === 'notReady' ? reason : null,
      };
    case 'M24_PUBLISH_ALREADY_LIVE':
      return { kind: 'busy' };
    case null:
      return { kind: 'generic' };
    default:
      return { kind: 'generic' };
  }
}
